import instance from "api/cmsapi";
import { resolveScoreSheetSummaryPayload } from "utils/dpatManualScoreHelpers";
import { DPAT_PASS_MARK_PERCENT } from "utils/utils";
import {
  flattenDistrictsWithRegions,
  buildRegionFilterOptions,
} from "../apr-dashboard/aprWorkflowStats";

export { flattenDistrictsWithRegions, buildRegionFilterOptions };

const EXCLUDED_DISTRICT_IDS = new Set(["EipJ1KBgsce", "EmVZbr0kApz"]);
const FINAL_SUMMARY_CONCURRENCY = 20;
const finalOutcomeCache = new Map();
const systemScoreCache = new Map();

export function classifyDpatDashboardPercent(percent) {
  const n = Number(percent);
  if (!Number.isFinite(n)) return "Poor";
  if (n >= 75) return "Excellent";
  if (n >= 60) return "Very Good";
  if (n >= 40) return "Good";
  if (n >= 30) return "Fair";
  return "Poor";
}

export const CI_MAIN_COUNT = 4;

export const CI_MAIN_INDICATORS = [
  {
    key: "1",
    code: "CI 1",
    thematicArea: "General Assembly Meetings and Approvals",
    subs: [
      { key: "1.1", code: "CI 1.1", label: "General Assembly Meetings" },
      { key: "1.2", code: "CI 1.2", label: "AAP and Budget Approval" },
      { key: "1.3", code: "CI 1.3", label: "Sub-Structure Meetings" },
    ],
  },
  {
    key: "2",
    code: "CI 2",
    thematicArea: "Other Statutory Meetings / Requirements",
    subs: [
      { key: "2.1", code: "CI 2.1", label: "Executive Committee Members" },
      { key: "2.2", code: "CI 2.2", label: "Sub-Structure Committee Meetings" },
      { key: "2.3", code: "CI 2.3", label: "Management Meetings" },
      { key: "2.4", code: "CI 2.4", label: "PRCC Meetings" },
      { key: "2.5", code: "CI 2.5", label: "Spatial Planning Committee (SPC)" },
    ],
  },
  {
    key: "3",
    code: "CI 3",
    thematicArea: "Public Financial Management and Auditing",
    subs: [
      { key: "3.1", code: "CI 3.1", label: "Entity Tender Committee" },
      { key: "3.2", code: "CI 3.2", label: "Internal Audit Unit Functionality" },
      { key: "3.3", code: "CI 3.3", label: "Audit Committee Meetings" },
    ],
  },
  {
    key: "4",
    code: "CI 4",
    thematicArea: "Transparency, Accountability & Participation",
    subs: [
      { key: "4.1", code: "CI 4.1", label: "Client Service Functionality" },
      { key: "4.2", code: "CI 4.2", label: "AAP Publication" },
      { key: "4.3", code: "CI 4.3", label: "Auditor-General Report to GA" },
      { key: "4.4", code: "CI 4.4", label: "Town Hall Meetings" },
    ],
  },
];

export const CI_SUB_INDICATORS = CI_MAIN_INDICATORS.flatMap((parent) =>
  parent.subs.map((sub) => ({ ...sub, major: parent.key })),
);

function parseCiCode(no) {
  const text = String(no || "").trim();
  if (!text || text.toLowerCase().includes("sub-total")) return null;
  const pairs = [...text.matchAll(/(\d+)\.(\d+)/g)];
  if (pairs.length) {
    const last = pairs[pairs.length - 1];
    const major = last[1];
    const minor = last[2];
    if (minor !== "0") return { type: "sub", key: `${major}.${minor}`, major };
    return { type: "parent", key: major, major };
  }
  const whole = text.match(/(?:CI[.\s-]*)?(\d+)\s*$/i) || text.match(/(?:^|[^\d])(\d+)(?:\s|$)/);
  return whole ? { type: "parent", key: whole[1], major: whole[1] } : null;
}

export function extractCiBreakdown(complianceIndicators, fulfilledKey = "fulfilled") {
  const byParent = new Map();
  const bySub = new Map();
  (complianceIndicators || []).forEach((item) => {
    const parsed = parseCiCode(item?.no);
    if (!parsed) return;
    const rawFulfilled = item?.[fulfilledKey];
    const payload = {
      no: item.no,
      thematicArea: item.thematicArea || "",
      fulfilled: rawFulfilled == null && fulfilledKey !== "fulfilled" ? null : Boolean(rawFulfilled ?? item.fulfilled),
    };
    if (parsed.type === "sub") bySub.set(parsed.key, payload);
    else byParent.set(parsed.key, payload);
  });

  const parents = CI_MAIN_INDICATORS.map((meta) => {
    const found = byParent.get(meta.key);
    const childStatuses = meta.subs.map((sub) => bySub.get(sub.key)?.fulfilled);
    const knownChildren = childStatuses.filter((value) => value !== undefined);
    let fulfilled = found ? found.fulfilled : null;
    if (fulfilled == null && knownChildren.length === meta.subs.length) {
      fulfilled = knownChildren.every(Boolean);
    }
    return {
      key: meta.key,
      code: meta.code,
      thematicArea: found?.thematicArea || meta.thematicArea,
      fulfilled,
    };
  });

  const subs = CI_SUB_INDICATORS.map((meta) => {
    const found = bySub.get(meta.key);
    return {
      ...meta,
      thematicArea: found?.thematicArea || meta.label,
      fulfilled: found ? found.fulfilled : null,
    };
  });

  return { parents, subs };
}

export function computeOverallPercent(totals) {
  const max = Number(totals?.grandTotalMaxScore);
  const actual = Number(totals?.grandTotalActualScore);
  if (!max || max <= 0 || !Number.isFinite(actual)) return null;
  return (actual / max) * 100;
}

function finiteOr(value, fallback) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

function rolePercent(actual, max) {
  const a = Number(actual);
  const m = Number(max);
  if (!m || m <= 0 || !Number.isFinite(a)) return null;
  return (a / m) * 100;
}

function dashboardRowFromFinalSummary(yearRow, meta, resolved) {
  const totals = resolved?.totals || {};
  const overallPercent = computeOverallPercent(totals);
  const breakdown = extractCiBreakdown(resolved?.complianceIndicators);
  const technicalBreakdown = extractCiBreakdown(
    resolved?.complianceIndicators,
    "technicalTeamStandingFulfilled",
  );
  const systemBreakdown = extractCiBreakdown(resolved?.complianceIndicators, "systemFulfilled");
  const parentsKnown = breakdown.parents.some((item) => item.fulfilled != null);
  const ciFulfilled = breakdown.parents.filter((item) => item.fulfilled === true).length;
  const technicalPercent = rolePercent(
    totals.technicalTeamTotalScore,
    totals.grandTotalMaxScore ?? yearRow.overallMax,
  );
  const technicalParentsKnown = technicalBreakdown.parents.some((item) => item.fulfilled != null);
  const systemParentsKnown = systemBreakdown.parents.some((item) => item.fulfilled != null);

  return {
    ...yearRow,
    ...meta,
    overallScore: finiteOr(totals.grandTotalActualScore, yearRow.overallScore),
    overallMax: finiteOr(totals.grandTotalMaxScore, yearRow.overallMax),
    overallPercent: overallPercent != null ? overallPercent : yearRow.overallPercent,
    sdiActual: finiteOr(totals.sdiTotalActualScore, yearRow.sdiActual),
    sdiMax: finiteOr(totals.sdiTotalMaxScore, yearRow.sdiMax),
    piActual: finiteOr(totals.piTotalActualScore, yearRow.piActual),
    piMax: finiteOr(totals.piTotalMaxScore, yearRow.piMax),
    complianceFulfilled: parentsKnown ? ciFulfilled : yearRow.complianceFulfilled,
    complianceTotal: CI_MAIN_COUNT,
    ciIndicators: breakdown.parents,
    classification:
      overallPercent != null
        ? classifyDpatDashboardPercent(overallPercent)
        : yearRow.classification,
    hasData: overallPercent != null || yearRow.overallPercent != null,
    technicalOverallScore: finiteOr(totals.technicalTeamTotalScore, yearRow.overallScore),
    technicalSdiActual: finiteOr(totals.sdiTechnicalTeamTotalScore, yearRow.sdiActual),
    technicalPiActual: finiteOr(totals.piTechnicalTeamTotalScore, yearRow.piActual),
    technicalOverallPercent: technicalPercent != null ? technicalPercent : yearRow.overallPercent,
    technicalClassification:
      technicalPercent != null
        ? classifyDpatDashboardPercent(technicalPercent)
        : yearRow.classification,
    technicalCiIndicators: technicalBreakdown.parents,
    technicalComplianceFulfilled: technicalParentsKnown
      ? technicalBreakdown.parents.filter((item) => item.fulfilled === true).length
      : yearRow.complianceFulfilled,
    technicalHasData: technicalPercent != null || yearRow.overallPercent != null,
    systemCiIndicators: systemBreakdown.parents,
    systemComplianceFulfilled: systemParentsKnown
      ? systemBreakdown.parents.filter((item) => item.fulfilled === true).length
      : yearRow.complianceFulfilled,
  };
}

export function mapRowsToTechnicalTeamScores(rows) {
  return (rows || []).map((row) => ({
    ...row,
    overallScore: row.technicalOverallScore ?? row.overallScore,
    overallPercent: row.technicalOverallPercent ?? row.overallPercent,
    sdiActual: row.technicalSdiActual ?? row.sdiActual,
    piActual: row.technicalPiActual ?? row.piActual,
    classification: row.technicalClassification ?? row.classification,
    ciIndicators: row.technicalCiIndicators ?? row.ciIndicators,
    complianceFulfilled: row.technicalComplianceFulfilled ?? row.complianceFulfilled,
    hasData: row.technicalHasData ?? row.hasData,
  }));
}

export function mapRowsToSystemCiScores(rows) {
  return (rows || []).map((row) => ({
    ...row,
    ciIndicators: row.systemCiIndicators ?? row.ciIndicators,
    complianceFulfilled: row.systemComplianceFulfilled ?? row.complianceFulfilled,
  }));
}

async function mapPool(items, limit, mapper) {
  const out = new Array(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (next < items.length) {
      const index = next;
      next += 1;
      out[index] = await mapper(items[index], index);
    }
  });
  await Promise.all(workers);
  return out;
}

/**
 * Year list (system scores). Cached so the ranking table can switch without a second wait.
 */
export async function fetchSystemScoreRows(districtRows, year, signal) {
  const y = String(year);
  const cached = systemScoreCache.get(y);
  if (cached) return cached;

  const summaryResponse = await instance.get(
    `comments/summary/year/${y}/DPAT/assessment_start_DAPT`,
    { signal }
  );

  const summaries = (summaryResponse.data || []).filter(
    (summary) => !EXCLUDED_DISTRICT_IDS.has(summary.districtId),
  );

  const districtMetaMap = new Map(
    districtRows
      .filter((r) => !EXCLUDED_DISTRICT_IDS.has(r.districtId))
      .map((r) => [r.districtId, r])
  );

  const yearRows = summaries.map((summary) => {
    const meta = districtMetaMap.get(summary.districtId) || {};
    return {
      ...summary,
      ...meta,
      hasData: summary.overallPercent != null,
      classification:
        summary.overallPercent != null
          ? classifyDpatDashboardPercent(summary.overallPercent)
          : summary.classification,
    };
  });

  if (!signal?.aborted) {
    systemScoreCache.set(y, yearRows);
  }
  return yearRows;
}

/**
 * Final Outcome from comments/summary/{district} only — Petition > QA > Technical > system.
 * Does not paint system scores first; those stay cached for the System Score toggle.
 */
export async function fetchDistrictDpatScores(
  districtRows,
  year,
  signal,
) {
  const y = String(year);
  const cached = finalOutcomeCache.get(y);
  if (cached) return cached;

  const yearRows = await fetchSystemScoreRows(districtRows, year, signal);

  const finalRows = await mapPool(yearRows, FINAL_SUMMARY_CONCURRENCY, async (row) => {
    if (signal?.aborted) {
      throw new Error("Aborted");
    }
    if (!row.districtId || !row.hasData) return row;

    try {
      const summaryRes = await instance.get(
        `comments/summary/${row.districtId}/${y}/DPAT`,
        { signal },
      );
      const resolved = resolveScoreSheetSummaryPayload(summaryRes?.data, [], {
        districtId: row.districtId,
        districtName: row.districtName,
      });
      return dashboardRowFromFinalSummary(row, {}, resolved);
    } catch (error) {
      if (signal?.aborted || error?.code === "ERR_CANCELED") throw error;
      console.error(
        `DPAT Final Outcome load failed for ${row.districtId}:`,
        error,
      );
      return row;
    }
  });

  if (!signal?.aborted) {
    finalOutcomeCache.set(y, finalRows);
  }
  return finalRows;
}

function pct(num, den) {
  if (!den || den <= 0) return "0.0";
  return ((Number(num) / den) * 100).toFixed(1);
}

function derivedStatus(rateStr) {
  const n = parseFloat(rateStr);
  if (n >= 70) return "excellent";
  if (n >= 40) return "good";
  if (n >= 15) return "warning";
  return "critical";
}

function avgPercent(rows) {
  const withData = rows.filter((r) => r.overallPercent != null);
  if (!withData.length) return null;
  return withData.reduce((s, r) => s + r.overallPercent, 0) / withData.length;
}

function assignRanks(sortedRows, scoreKey = "overallPercent") {
  let rank = 0;
  let prevScore = null;
  return sortedRows.map((row, index) => {
    const score = row[scoreKey];
    if (score !== prevScore) {
      rank = index + 1;
      prevScore = score;
    }
    return { ...row, rank };
  });
}

export function buildNationalDistrictRanking(rows) {
  const withData = rows.filter((r) => r.hasData && r.overallPercent != null);
  const sorted = [...withData].sort((a, b) => {
    if (b.overallPercent !== a.overallPercent) return b.overallPercent - a.overallPercent;
    return a.districtName.localeCompare(b.districtName);
  });
  return assignRanks(sorted);
}

export function buildNationalCiRanking(rows) {
  const withCi = (rows || []).filter((r) => r.hasData && Number(r.complianceTotal) > 0);
  const sorted = [...withCi].sort((a, b) => {
    const ca = Number(a.complianceFulfilled) || 0;
    const cb = Number(b.complianceFulfilled) || 0;
    if (cb !== ca) return cb - ca;
    return String(a.districtName || "").localeCompare(String(b.districtName || ""));
  });
  return assignRanks(sorted, "complianceFulfilled");
}

export function buildRegionalDistrictRankings(rows) {
  const byRegion = new Map();
  for (const row of rows) {
    if (!byRegion.has(row.regionKey)) {
      byRegion.set(row.regionKey, { regionKey: row.regionKey, regionName: row.regionName, rows: [] });
    }
    byRegion.get(row.regionKey).rows.push(row);
  }

  const out = [];
  for (const [, grp] of byRegion) {
    const withData = grp.rows.filter((r) => r.hasData && r.overallPercent != null);
    const sorted = [...withData].sort((a, b) => {
      if (b.overallPercent !== a.overallPercent) return b.overallPercent - a.overallPercent;
      return a.districtName.localeCompare(b.districtName);
    });
    const ranked = assignRanks(sorted);
    const regionAvg = avgPercent(withData);
    out.push({
      regionKey: grp.regionKey,
      regionName: grp.regionName,
      districtCount: grp.rows.length,
      assessedCount: withData.length,
      regionAveragePercent: regionAvg,
      districts: ranked,
    });
  }

  out.sort((a, b) => String(a.regionName).localeCompare(String(b.regionName)));
  return out;
}

export function buildNationalRegionRanking(rows) {
  const regional = buildRegionalDistrictRankings(rows);
  const ranked = regional
    .filter((r) => r.regionAveragePercent != null)
    .map((r) => ({
      regionKey: r.regionKey,
      regionName: r.regionName,
      districtCount: r.districtCount,
      assessedCount: r.assessedCount,
      regionAveragePercent: r.regionAveragePercent,
      classification: classifyDpatDashboardPercent(r.regionAveragePercent),
    }))
    .sort((a, b) => {
      if (b.regionAveragePercent !== a.regionAveragePercent) {
        return b.regionAveragePercent - a.regionAveragePercent;
      }
      return a.regionName.localeCompare(b.regionName);
    });

  return assignRanks(ranked, "regionAveragePercent");
}

export function filterDpatScoreRows(rows, { regionKey = "all", dataFilter = "all", search = "" }) {
  const q = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (regionKey !== "all" && row.regionKey !== regionKey) return false;
    if (dataFilter === "assessed" && !row.hasData) return false;
    if (dataFilter === "missing" && row.hasData) return false;
    if (q) {
      const hay = `${row.districtName} ${row.regionName} ${row.classification || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export const DPAT_DATA_FILTER_OPTIONS = [
  { value: "all", label: "All districts" },
  { value: "assessed", label: "With DPAT score" },
  { value: "missing", label: "No score data" },
];

export function summarizeDpatScores(rows) {
  const totalDistricts = rows.length;
  const assessed = rows.filter((r) => r.hasData);
  const assessedCount = assessed.length;
  const missingCount = totalDistricts - assessedCount;

  const nationalAvg = avgPercent(assessed);
  const assessedRate = pct(assessedCount, totalDistricts);

  const classificationCounts = {
    Excellent: 0,
    "Very Good": 0,
    Good: 0,
    Fair: 0,
    Poor: 0,
  };
  for (const r of assessed) {
    if (r.classification && classificationCounts[r.classification] !== undefined) {
      classificationCounts[r.classification]++;
    }
  }

  const performanceBars = Object.entries(classificationCounts).map(([tier, count]) => ({
    tier,
    count,
  }));

  const topDistricts = buildNationalDistrictRanking(rows).slice(0, 10);
  const bottomDistricts = [...assessed]
    .sort((a, b) => a.overallPercent - b.overallPercent)
    .slice(0, 5)
    .map((r, i) => ({ ...r, rank: i + 1 }));

  const regionRanking = buildNationalRegionRanking(rows);
  const regionalGroups = buildRegionalDistrictRankings(rows);

  const reportKpis = [
    {
      title: "Districts Assemblies Assessed",
      description: "Districts with DPAT Final Outcome data for the year",
      value: assessedRate,
      unit: "%",
      status: derivedStatus(assessedRate),
    },
    {
      title: "National average score",
      description: "Mean Final Outcome percentage across assessed districts",
      value: nationalAvg != null ? nationalAvg.toFixed(1) : "—",
      unit: nationalAvg != null ? "%" : "",
      status: nationalAvg != null ? derivedStatus(String(nationalAvg)) : "warning",
    },
    {
      title: "Regions with data",
      description: "Regions where at least one district has DPAT scores",
      value: String(regionRanking.length),
      unit: ` / ${new Set(rows.map((r) => r.regionKey)).size || "—"} regions`,
      status: "good",
    },
  ];

  const sdiAvg =
    assessed.length > 0
      ? assessed.reduce((s, r) => {
        const max = Number(r.sdiMax);
        const actual = Number(r.sdiActual);
        if (!max) return s;
        return s + (actual / max) * 100;
      }, 0) / assessed.filter((r) => r.sdiMax).length
      : null;

  const piAvg =
    assessed.length > 0
      ? assessed.reduce((s, r) => {
        const max = Number(r.piMax);
        const actual = Number(r.piActual);
        if (!max) return s;
        return s + (actual / max) * 100;
      }, 0) / assessed.filter((r) => r.piMax).length
      : null;

  const ciAvgRows = assessed.filter((r) => Number(r.complianceTotal) > 0);
  const ciAvg =
    ciAvgRows.length > 0
      ? ciAvgRows.reduce(
          (s, r) => s + (Number(r.complianceFulfilled) / Number(r.complianceTotal)) * 100,
          0,
        ) / ciAvgRows.length
      : null;

  const regionAvgChart = regionRanking.map((r) => ({
    region: r.regionName.replace(/ Region$/i, ""),
    average: Number(r.regionAveragePercent.toFixed(1)),
    assessed: r.assessedCount,
  }));

  const passCount = assessed.filter((r) => r.overallPercent >= DPAT_PASS_MARK_PERCENT).length;
  const failCount = assessed.length - passCount;

  const passFailChart = [
    { name: `Pass (≥${DPAT_PASS_MARK_PERCENT}%)`, value: passCount, fillKey: "emerald" },
    { name: `Below pass`, value: failCount, fillKey: "coral" },
  ].filter((d) => d.value > 0);

  const tierPieChart = Object.entries(classificationCounts)
    .filter(([, count]) => count > 0)
    .map(([name, value]) => ({ name, value }));

  const sdiPiChart =
    sdiAvg != null || piAvg != null || ciAvg != null
      ? [
        { pillar: "Service Delivery (SDI)", percent: sdiAvg != null ? Number(sdiAvg.toFixed(1)) : 0 },
        { pillar: "Performance (PI)", percent: piAvg != null ? Number(piAvg.toFixed(1)) : 0 },
        { pillar: "Compliance (CI)", percent: ciAvg != null ? Number(ciAvg.toFixed(1)) : 0 },
      ]
      : [];

  const scoreBuckets = [
    { range: "0–10%", min: 0, max: 10 },
    { range: "11–20%", min: 11, max: 20 },
    { range: "21–30%", min: 21, max: 30 },
    { range: "31–40%", min: 31, max: 40 },
    { range: "41–50%", min: 41, max: 50 },
    { range: "51–60%", min: 51, max: 60 },
    { range: "61–75%", min: 61, max: 75 },
    { range: "76–100%", min: 76, max: 100 },
  ].map(({ range, min, max }) => ({
    range,
    count: assessed.filter((r) => r.overallPercent >= min && r.overallPercent <= max).length,
  }));

  const compliancePct = (r) => {
    if (!r.complianceTotal) return null;
    return (r.complianceFulfilled / r.complianceTotal) * 100;
  };

  const complianceBands = [
    {
      band: "100% fulfilled",
      count: assessed.filter((r) => compliancePct(r) === 100).length,
    },
    {
      band: "75–99%",
      count: assessed.filter((r) => {
        const p = compliancePct(r);
        return p != null && p >= 75 && p < 100;
      }).length,
    },
    {
      band: "50–74%",
      count: assessed.filter((r) => {
        const p = compliancePct(r);
        return p != null && p >= 50 && p < 75;
      }).length,
    },
    {
      band: "1–49%",
      count: assessed.filter((r) => {
        const p = compliancePct(r);
        return p != null && p > 0 && p < 50;
      }).length,
    },
    {
      band: "None fulfilled",
      count: assessed.filter((r) => compliancePct(r) === 0).length,
    },
  ].filter((d) => d.count > 0);

  const regionCoverageChart = regionalGroups
    .map((g) => ({
      region: g.regionName.replace(/ Region$/i, ""),
      assessed: g.assessedCount,
      missing: g.districtCount - g.assessedCount,
    }))
    .sort((a, b) => b.assessed - a.assessed)
    .slice(0, 10);

  const medianPercent = (() => {
    const vals = assessed.map((r) => r.overallPercent).sort((a, b) => a - b);
    if (!vals.length) return null;
    const mid = Math.floor(vals.length / 2);
    return vals.length % 2 ? vals[mid] : (vals[mid - 1] + vals[mid]) / 2;
  })();

  return {
    totalDistricts,
    assessedCount,
    missingCount,
    nationalAvg,
    assessedRate,
    classificationCounts,
    performanceBars,
    topDistricts,
    bottomDistricts,
    regionRanking,
    reportKpis,
    sdiAvg,
    piAvg,
    ciAvg,
    regionAvgChart,
    passCount,
    failCount,
    passFailChart,
    tierPieChart,
    sdiPiChart,
    scoreBuckets,
    complianceBands,
    regionCoverageChart,
    medianPercent,
  };
}

export function getPerformanceBadgeClass(classification) {
  switch (classification) {
    case "Excellent":
      return "bg-emerald-50 text-emerald-800 border-emerald-200";
    case "Very Good":
      return "bg-green-50 text-green-800 border-green-200";
    case "Good":
      return "bg-sky-50 text-sky-800 border-sky-200";
    case "Fair":
      return "bg-amber-50 text-amber-800 border-amber-200";
    default:
      return "bg-red-50 text-red-800 border-red-200";
  }
}
